import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, User, MapPin, Clock, DollarSign, FileText, CreditCard } from 'lucide-react';
import { SPORT_STYLES, SPORT_FALLBACK } from './constants';

const STATUS_LABEL = {
  CONFIRMED: { label: 'Confirmed', cls: 'bg-green-100 text-green-900 border-green-300' },
  AWAITING_PAYMENT: { label: 'Awaiting Payment', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
};

export default function BookingPopover({ booking, open, onClose }) {
  const navigate = useNavigate();
  if (!booking) return null;

  const sty = SPORT_STYLES[booking.primary_sport] || SPORT_FALLBACK;
  const stat = STATUS_LABEL[booking.status] || { label: booking.status, cls: '' };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="booking-popover">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
              #{booking.booking_ref}
            </span>
            <Badge className={sty.badge}>{sty.label}</Badge>
            <Badge variant="outline" className={stat.cls}>
              {stat.label}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <Row icon={<User className="w-4 h-4" />} label="Customer" value={booking.customer_name} />
          {booking.customer_phone && (
            <Row icon={<Phone className="w-4 h-4" />} label="Phone" value={booking.customer_phone} />
          )}
          <Row
            icon={<Clock className="w-4 h-4" />}
            label="Time"
            value={`${booking.time_slot} · ${booking.duration}h`}
          />
          <Row
            icon={<MapPin className="w-4 h-4" />}
            label="Courts"
            value={(booking.courts || [])
              .map((c) => `Court ${c.court_number} (${c.sport})`)
              .join(', ')}
          />
          <Row
            icon={<DollarSign className="w-4 h-4" />}
            label="Total"
            value={`$${(booking.final_total ?? 0).toFixed(2)}`}
          />
          {booking.payment_method && (
            <Row
              icon={<CreditCard className="w-4 h-4" />}
              label="Payment"
              value={booking.payment_method}
            />
          )}
          {booking.special_requests && (
            <Row
              icon={<FileText className="w-4 h-4" />}
              label="Notes"
              value={booking.special_requests}
            />
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="booking-popover-close">
            Close
          </Button>
          <Button
            onClick={() => {
              navigate(`/admin/bookings?ref=${booking.booking_ref}`);
              onClose();
            }}
            data-testid="booking-popover-view-btn"
          >
            View Booking →
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground mt-0.5">{icon}</span>
      <div className="flex-1">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );
}
